import { createContext, useState } from 'react';
import { Skeleton, Spin, Table, Menu } from 'antd';
import { Notify } from '../services/notificationService';
interface LoadingContent {
  title?: string;
  description?: string;
}

interface LoaderContextType {
  loading: boolean;
  showSkeleton: boolean;
  loadingContent?: LoadingContent;
  setAppLoading: (
    loading: boolean,
    showSkeleton?: boolean,
    loadingContent?: LoadingContent
  ) => void;
}

const LoaderContext = createContext<LoaderContextType>({
  loading: false,
  showSkeleton: false,
  setAppLoading: () => null,
});
// Expose a global loader controller
let loaderController: LoaderContextType;

export const LoaderProvider = () => {
  const [loading, setLoading] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [loadingContent, setLoadingContent] = useState<LoadingContent | null>(
    null
  );

  const setAppLoading = (
    loading: boolean,
    showSkeleton = false,
    loadingContent: LoadingContent | null = null
  ) => {
    setLoading(loading);
    setShowSkeleton(showSkeleton);
    setLoadingContent(loadingContent);
  };

  const contextValue = { loading, showSkeleton, setAppLoading };
  loaderController = contextValue;

  return (
    <LoaderContext.Provider value={contextValue}>
      {showSkeleton ? (
        <div className="loading-skeleton">
          <SkeletonSideMenu />
          <SkeletonMainContent loadingContent={loadingContent} />
        </div>
      ) : (
        <Spin spinning={loading} fullscreen />
      )}
    </LoaderContext.Provider>
  );
};

// Function to get access to the loader controller
export const getAppLoaderController = (): LoaderContextType => {
  if (!loaderController) {
    Notify.error('LoaderProvider is not initialized.');
    return {
      loading: false,
      showSkeleton: false,
      setAppLoading: () => null,
    };
  }
  return loaderController;
};

// Table Skeleton Component
const SkeletonTable = () => {
  const columns = [
    {
      title: <Skeleton.Input active size="small" />,
      dataIndex: 'col1',
      key: 'col1',
    },
    {
      title: <Skeleton.Input active size="small" />,
      dataIndex: 'col2',
      key: 'col2',
    },
    {
      title: <Skeleton.Input active size="small" />,
      dataIndex: 'col3',
      key: 'col3',
    },
    {
      title: <Skeleton.Input active size="small" />,
      dataIndex: 'col4',
      key: 'col4',
    },
  ];

  const data = Array.from({ length: 6 }, (_, i) => ({
    key: i,
    col1: <Skeleton.Input active size="small" />,
    col2: <Skeleton.Input active size="small" />,
    col3: <Skeleton.Input active size="small" />,
    col4: <Skeleton.Input active size="small" />,
  }));

  return <Table columns={columns} dataSource={data} pagination={false} />;
};

const SkeletonSideMenu = () => {
  const menuItems = Array.from({ length: 4 }).map((_, index) => ({
    key: index.toString(),
    label: <Skeleton.Input active size="small" style={{ width: 120 }} />,
  }));

  return (
    <div className="skeleton-side-menu">
      <Menu mode="inline" items={menuItems} />
    </div>
  );
};

const SkeletonMainContent = ({
  loadingContent,
}: {
  loadingContent: LoadingContent | null;
}) => (
  <div className="skeleton-main-content">
    <Skeleton active title paragraph={{ rows: 1 }} />
    <SkeletonTable />
    {loadingContent && (
      <div className="loading-skeleton-content">
        <h1>{loadingContent.title}</h1>
        <p>{loadingContent.description}</p>
      </div>
    )}
  </div>
);
